import {Box, Collapse, Indicator, Radio, Select, Text} from '@mantine/core';
import {BasicButton} from '@packages/ui';
import Head from 'next/head';
import type React from 'react';
import {useEffect, useState} from 'react';
import {MdExpandLess, MdFilterAlt, MdSort} from 'react-icons/md';
import {
  HOME_CRUMB,
  RECRUITING_INFO_CRUMB,
} from '../../../../../config/constants/crumb';
import {HOME, RECRUITING_INFO} from '../../../../../config/constants/routes';
import type {
  GraduationYearsHotReturnValueType,
  RecruitmentTagsReturnValueType,
  RecruitmentTypesReturnValueType,
} from '../../../../../core/repositories/recruitingInfoRepository';
import type {IndustryCategoriesReturnValueType} from '../../../../../core/repositories/reportsRepository';
import {useAuth} from '../../../../../core/usecases/useAuth';
import {BreadcrumbsComponent} from '../../../../../shared/components/BreadcrumbsComponent';
import {createBreadCrumbsStructuredData} from '../../../../../shared/services/createBreadCrumbsStructuredData';
import type {RecruitmentOrderType} from '../../../usecase/useHandleRecruitingInfoForSp';
import {TagForEngineer} from './TagForEngineer';

export const sortOrderChoices = [
  {
    label: 'おすすめ順',
    value: 'recommend',
  },
  {
    value: 'recent',
    label: '新着順',
  },
  {
    value: 'deadline',
    label: '締切順',
  },
  {
    value: 'eventDate',
    label: '開催順',
  },
] satisfies Array<{
  value: string;
  label: string;
}>;

type Props = {
  industryCategories?: IndustryCategoriesReturnValueType;
  recruitmentTags?: RecruitmentTagsReturnValueType;
  recruitmentTypes?: RecruitmentTypesReturnValueType;
  graduationYearsHots?: GraduationYearsHotReturnValueType;
  filterCategories: number[];
  filterRecruitmentTags: number[];
  filterRecruitmentTypes: number[];
  filterGraduationYear?: number;
  recruitmentOrder: RecruitmentOrderType;
  onFilterCategoryChange: (id: number) => void;
  onFilterRecruitmentTagChange: (id: number) => void;
  onFilterRecruitmentTypeChange: (id: number) => void;
  onGraduationYearChange: (year: number | undefined) => void;
  onOrderChange: (order: RecruitmentOrderType) => void;
  onFilterApply: () => void;
  isDrawerOpen: boolean;
  onDrawerOpenChange: (isOpen: boolean) => void;
  isOrderDrawerOpen: boolean;
  onOrderDrawerOpenChange: (isOpen: boolean) => void;
};

export const HeaderForSp: React.FC<Props> = ({
  industryCategories,
  recruitmentTags,
  recruitmentTypes,
  graduationYearsHots,
  filterCategories,
  filterRecruitmentTags,
  filterRecruitmentTypes,
  filterGraduationYear,
  recruitmentOrder,
  onFilterCategoryChange,
  onFilterRecruitmentTagChange,
  onFilterRecruitmentTypeChange,
  onGraduationYearChange,
  onOrderChange,
  onFilterApply,
  isDrawerOpen,
  onDrawerOpenChange,
  isOrderDrawerOpen,
  onOrderDrawerOpenChange,
}) => {
  const [filterCategoriesTmp, setFilterCategoriesTmp] =
    useState(filterCategories);
  const [filterRecruitmentTagsTmp, setFilterRecruitmentTagsTmp] = useState(
    filterRecruitmentTags,
  );
  const [filterRecruitmentTypesTmp, setFilterRecruitmentTypesTmp] = useState(
    filterRecruitmentTypes,
  );
  const [filterGraduationYearTmp, setFilterGraduationYearTmp] =
    useState(filterGraduationYear);
  const [recruitmentOrderTmp, setRecruitmentOrderTmp] =
    useState(recruitmentOrder);

  useEffect(() => {
    setFilterCategoriesTmp(filterCategories);
    setFilterRecruitmentTagsTmp(filterRecruitmentTags);
    setFilterRecruitmentTypesTmp(filterRecruitmentTypes);
    setFilterGraduationYearTmp(filterGraduationYear);
    setRecruitmentOrderTmp(recruitmentOrder);
  }, [
    filterCategories,
    filterRecruitmentTags,
    filterRecruitmentTypes,
    filterGraduationYear,
    recruitmentOrder,
    isDrawerOpen,
  ]);

  const crumbs = [
    {label: HOME_CRUMB, href: HOME},
    {label: RECRUITING_INFO_CRUMB, href: RECRUITING_INFO},
  ];

  const totalFilterCount =
    filterCategories.length +
    filterRecruitmentTags.length +
    filterRecruitmentTypes.length +
    (filterGraduationYear === 0 || filterGraduationYear === undefined ? 0 : 1);

  const currentOrderLabel =
    sortOrderChoices.find((c) => c.value === recruitmentOrder)?.label ??
    'おすすめ順';

  const {user} = useAuth();
  const isEngineer = user?.isEngineer ?? false;

  return (
    <Box className="bg-background">
      <Head>
        <script type="application/ld+json">
          {JSON.stringify(createBreadCrumbsStructuredData(crumbs))}
        </script>
      </Head>
      <Box className="px-16 py-8 font-bold">
        <BreadcrumbsComponent crumbs={crumbs} />
      </Box>
      <Box className="px-16 py-12 bg-white">
        <Text className="text-xl font-bold leading-snug">募集を探す</Text>
        <Box className="text-base">
          トップ企業限定の本選考・説明会・セミナー情報を日々更新！まずはここをチェック！
        </Box>
      </Box>
      <Box className="flex justify-between bg-white p-8 shadow-card">
        <Box>
          <button
            onClick={() => {
              onDrawerOpenChange(!isDrawerOpen);
            }}
            className="flex h-[32px] w-[100px] items-center gap-x-4 text-black"
            aria-expanded={isDrawerOpen}
            aria-controls="filter-drawer"
            aria-label={
              isDrawerOpen
                ? '絞り込みドロワーを閉じる'
                : '絞り込みドロワーを開く'
            }
          >
            {totalFilterCount > 0 ? (
              <>
                <Indicator size={6} color="red">
                  <MdFilterAlt size="20px" aria-hidden="true" />
                </Indicator>
                <Text fz="xs" className="font-bold">
                  {isDrawerOpen ? '閉じる' : '絞り込む'}
                  <span className="rounded-full bg-black text-white text-center px-4 ml-[2px]">
                    {totalFilterCount > 9 ? '9+' : totalFilterCount}
                  </span>
                </Text>
              </>
            ) : (
              <>
                {isDrawerOpen ? (
                  <MdExpandLess size="24px" aria-hidden="true" />
                ) : (
                  <MdFilterAlt size="20px" aria-hidden="true" />
                )}
                <Text fz="xs" className="font-bold">
                  {isDrawerOpen ? '閉じる' : '絞り込む'}
                </Text>
              </>
            )}
          </button>
        </Box>
        <button
          className="p-4 flex gap-4 font-bold items-center text-black-1"
          onClick={() => {
            onOrderDrawerOpenChange(!isOrderDrawerOpen);
          }}
          aria-expanded={isOrderDrawerOpen}
          aria-controls="order-drawer"
        >
          <MdSort size={20} aria-hidden="true" />
          <Text fz="xs" className="font-bold">
            {currentOrderLabel}
          </Text>
        </button>
      </Box>

      <Collapse in={isDrawerOpen}>
        <Box
          id="filter-drawer"
          className="flex w-full bg-white shadow-md px-12 mt-4"
        >
          <div className="bg-white mt-8 mb-16 w-full">
            <Box className="text-center">
              <BasicButton
                children="絞り込む"
                type="button"
                className="mb-8 max-w-[600px] text-center"
                onClick={onFilterApply}
              />
            </Box>

            <Text className="mb-8 font-bold">対象で絞り込む</Text>
            {recruitmentTags?.ok &&
              recruitmentTags.value.tags.map((recruitmentTag) => (
                <div
                  key={recruitmentTag.id}
                  className="mb-8 flex items-center px-12"
                >
                  <input
                    type="checkbox"
                    id={`tag-${recruitmentTag.id}`}
                    className="mr-8 cursor-pointer"
                    checked={filterRecruitmentTagsTmp.includes(
                      recruitmentTag.id,
                    )}
                    onChange={() => {
                      onFilterRecruitmentTagChange(recruitmentTag.id);
                    }}
                    aria-label={recruitmentTag.longName}
                  />
                  <label
                    htmlFor={`tag-${recruitmentTag.id}`}
                    className="cursor-pointer select-none text-base text-black-1"
                  >
                    {recruitmentTag.longName}
                    {isEngineer && recruitmentTag.id === 3 && (
                      <TagForEngineer />
                    )}
                  </label>
                </div>
              ))}

            <Text className="mb-8 font-bold">業界で絞り込む</Text>
            {industryCategories?.ok &&
              industryCategories.value.industryCategories.map(
                (industryCategory) => (
                  <div
                    key={industryCategory.id}
                    className="mb-8 flex items-center px-12"
                  >
                    <input
                      type="checkbox"
                      id={`industry-${industryCategory.id}`}
                      className="mr-8 cursor-pointer"
                      checked={filterCategoriesTmp.includes(
                        industryCategory.id,
                      )}
                      onChange={() => {
                        onFilterCategoryChange(industryCategory.id);
                      }}
                      aria-label={industryCategory.name}
                    />
                    <label
                      htmlFor={`industry-${industryCategory.id}`}
                      className="cursor-pointer select-none text-base text-black-1"
                    >
                      {industryCategory.name}
                    </label>
                  </div>
                ),
              )}

            <Text className="mb-8 font-bold">種類で絞り込む</Text>
            {recruitmentTypes?.ok &&
              recruitmentTypes.value.types.map((recruitmentType) => (
                <div
                  key={recruitmentType.id}
                  className="mb-8 flex items-center px-12"
                >
                  <input
                    type="checkbox"
                    id={`type-${recruitmentType.id}`}
                    className="mr-8 cursor-pointer"
                    checked={filterRecruitmentTypesTmp.includes(
                      recruitmentType.id,
                    )}
                    onChange={() => {
                      onFilterRecruitmentTypeChange(recruitmentType.id);
                    }}
                    aria-label={recruitmentType.name}
                  />
                  <label
                    htmlFor={`type-${recruitmentType.id}`}
                    className="cursor-pointer select-none text-base text-black-1"
                  >
                    {recruitmentType.name}
                  </label>
                </div>
              ))}

            <Text className="mb-8 font-bold">対象卒年で絞り込む</Text>
            {graduationYearsHots?.ok && (
              <Select
                className="mb-8 flex items-center px-12"
                data={convertGraduationYears(graduationYearsHots.value.years)}
                value={
                  filterGraduationYearTmp === undefined
                    ? '0'
                    : filterGraduationYearTmp.toString()
                }
                onChange={(newValue) => {
                  onGraduationYearChange(
                    newValue ? Number(newValue) : undefined,
                  );
                }}
                aria-label="卒業年度"
              />
            )}

            <BasicButton
              children="絞り込む"
              type="button"
              className="mt-12 max-w-[600px]"
              onClick={onFilterApply}
            />
          </div>
        </Box>
      </Collapse>

      <Collapse in={isOrderDrawerOpen}>
        <Box
          id="order-drawer"
          className="flex w-full bg-white shadow-md px-12 mt-4"
        >
          <div className="bg-white mt-8 mb-16 w-full">
            <Text className="mb-8 font-bold">並び替え</Text>
            <Radio.Group
              value={recruitmentOrderTmp}
              onChange={(value) => {
                onOrderChange(value as RecruitmentOrderType);
              }}
              className="flex flex-col gap-4 px-12"
              size="xs"
            >
              {sortOrderChoices.map((choice) => (
                <Radio
                  key={choice.value}
                  value={choice.value}
                  label={choice.label}
                  className="cursor-pointer"
                  styles={{label: {fontSize: '16px'}}}
                />
              ))}
            </Radio.Group>
            <BasicButton
              children="絞り込む"
              type="button"
              className="mt-12 max-w-[600px]"
              onClick={onFilterApply}
            />
          </div>
        </Box>
      </Collapse>
    </Box>
  );
};

type ConvertedGraduationYearsHot = {
  value: string;
  label: string;
};

export function convertGraduationYears(
  inputData: number[],
): ConvertedGraduationYearsHot[] {
  return [
    {
      value: '0',
      label: 'すべて',
    },
    ...inputData.map((year) => ({
      value: year.toString(),
      label: `${year.toString()}卒`,
    })),
    {
      value: '-1',
      label: '卒年不問',
    },
  ];
}
